import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Package, CheckCircle, Pin } from "lucide-react";
import { generateShadeHex, isLightHex, qualities } from "./helpers";
import { StockBadge } from "@/components/StockBadge";
import useStockStatus from "@/hooks/useStockStatus";

function ColourSwatch({ family, num, idx, total, inCart, isPinned, onAdd, onPin }) {
  const hex = generateShadeHex(family.hex, idx, total);
  const light = isLightHex(hex);
  return (
    <div className="relative group">
      <button
        onClick={() => onAdd(num)}
        className={`w-full relative aspect-square rounded-sm border-2 flex flex-col items-center justify-center transition-all duration-200 ${
          inCart
            ? "border-[var(--mp-solid)] shadow-lg scale-105 ring-2 ring-[#101010]/20"
            : "border-transparent hover:scale-105 hover:shadow-md"
        }`}
        style={{ backgroundColor: hex }}
        data-testid={`colour-swatch-${num}`}
      >
        <span className={`text-[11px] font-bold ${light ? "text-gray-800" : "text-white"}`}>
          {num}
        </span>
        {inCart && (
          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--mp-solid)] flex items-center justify-center">
            <CheckCircle className="w-3 h-3 text-white" />
          </div>
        )}
      </button>
      <button
        onClick={() => onPin(num, hex)}
        aria-label={isPinned ? `Unpin shade ${num}` : `Pin shade ${num} to compare`}
        className={`absolute -bottom-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-200 ${
          isPinned ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
        }`}
        style={{
          backgroundColor: isPinned ? "var(--mp-copper-solid)" : "rgba(16,16,16,0.6)",
        }}
        data-testid={`colour-pin-${num}`}
      >
        <Pin className="w-2.5 h-2.5 text-white" />
      </button>
    </div>
  );
}

export default function SwatchesView({
  selectedFamily,
  selectedQuality,
  onQualityChange,
  cart,
  onAddToCart,
  pinnedKeys,
  onPin,
}) {
  const stock = useStockStatus();
  const currentQuality = qualities.find((q) => q.id === selectedQuality);

  return (
    <div>
      {/* Quality selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 bg-white border border-[var(--mp-line)] rounded-sm p-4">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4" style={{ color: "var(--mp-text-strong)" }} />
          <label
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          >
            Fabric Quality:
          </label>
        </div>
        <Select value={selectedQuality} onValueChange={onQualityChange}>
          <SelectTrigger
            className="w-full sm:w-72 rounded-sm border-[var(--mp-line)] text-sm"
            data-testid="quality-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {qualities.map((q) => (
              <SelectItem key={q.id} value={q.id}>
                {q.name} ({q.panna} panna - {q.meters_per_bundle}m/bundle)
                {stock[q.id] && stock[q.id] !== "ready"
                  ? ` · ${stock[q.id] === "low" ? "Low stock" : "Made to order"}`
                  : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span
          className="text-[11px] font-medium px-2 py-1 rounded-sm pastel-yellow"
          style={{ color: "var(--mp-copper-deep)" }}
        >
          {currentQuality.meters_per_bundle}m per bundle
        </span>
        <StockBadge status={stock[selectedQuality] || "ready"} />
      </div>

      {/* Swatches */}
      <div
        className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2 mb-8"
        data-testid="colour-swatches-grid"
      >
        {selectedFamily.colours.map((num, idx) => (
          <ColourSwatch
            key={num}
            family={selectedFamily}
            num={num}
            idx={idx}
            total={selectedFamily.colours.length}
            inCart={cart.some(
              (c) => c.colour_number === num && c.fabric_quality === selectedQuality
            )}
            isPinned={pinnedKeys.has(`${num}-${selectedQuality}`)}
            onAdd={onAddToCart}
            onPin={onPin}
          />
        ))}
      </div>

      <p
        className="text-xs text-center mb-4"
        style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
      >
        Click on a shade to add 1 bundle ({currentQuality.meters_per_bundle}m) to your cart. Click
        again to add more. Use the pin to compare shades side by side.
      </p>
    </div>
  );
}
